import { DialogComponent, DialogOpenEvent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';

/**
 * A custom element that manages a cart drawer.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number | null} */
  #reservedInterval = null;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.addEventListener(DialogOpenEvent.eventName, this.#handleDialogOpen);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener(DialogOpenEvent.eventName, this.#handleDialogOpen);
    if (this.#reservedInterval) clearInterval(this.#reservedInterval);
  }

  #handleCartAdd = () => {
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }
  };

  #handleDialogOpen = () => {
    const reservedEl = this.querySelector('.cart-shipping-progress__reserved--countdown');
    if (!reservedEl) return;
    const countdownEl = reservedEl.querySelector('.cart-shipping-progress__countdown');
    if (!countdownEl) return;
    const minutes = parseInt(reservedEl.dataset.reservedMinutes || '10', 10);
    let totalSeconds = minutes * 60;
    const format = (s) => {
      const m = Math.floor(s / 60);
      const sec = s % 60;
      return `${m.toString().padStart(2, '0')}:${sec.toString().padStart(2, '0')}`;
    };
    if (this.#reservedInterval) clearInterval(this.#reservedInterval);
    countdownEl.textContent = format(totalSeconds);
    this.#reservedInterval = setInterval(() => {
      totalSeconds--;
      countdownEl.textContent = format(Math.max(0, totalSeconds));
      if (totalSeconds <= 0 && this.#reservedInterval) {
        clearInterval(this.#reservedInterval);
        this.#reservedInterval = null;
      }
    }, 1000);
  };

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
